(() => {
  const TAG = 'pm-home-health-carousel';
  if (customElements.get(TAG)) return;

  class PMHomeHealthCarousel extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.viewport = this.querySelector('[data-pm-home-health-viewport]');
      this.track = this.querySelector('[data-pm-home-health-track]');
      this.slides = [...this.querySelectorAll('[data-pm-home-health-slide]')];
      this.dots = [...this.querySelectorAll('[data-pm-home-health-dot]')];
      this.status = this.querySelector('[data-pm-home-health-status]');
      if (!this.viewport || !this.track || this.slides.length < 2) return;

      this.index = 0;
      this.busy = false;
      this.dragging = false;
      this.reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this._abort = new AbortController();
      const signal = this._abort.signal;

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.go(index), { signal });
        dot.addEventListener('keydown', (event) => this.onDotKeydown(event, index), { signal });
      });
      this.viewport.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      this.unbindSwipe = window.PmSwipe?.bind(this.viewport, {
        threshold: 30,
        disabled: () => this.busy,
        onHorizontalLock: () => {
          this.dragging = true;
          this.viewport.dataset.dragging = 'true';
          this.track.style.transition = 'none';
        },
        onDrag: (deltaX) => this.renderDrag(deltaX),
        onCommit: (direction) => {
          this.dragging = false;
          delete this.viewport.dataset.dragging;
          this.go(this.index + direction, true);
        },
        onCancel: () => {
          this.dragging = false;
          delete this.viewport.dataset.dragging;
          this.render(true);
        }
      });

      this._resize = new ResizeObserver(() => {
        const width = this.viewport.clientWidth;
        if (width === this._viewportWidth) return;
        this._viewportWidth = width;
        this.render(false);
      });
      this._resize.observe(this.viewport);
      // Each card keeps its own content height; hidden cards cannot reserve
      // a tall empty panel under the shorter Eye/Skin or Long-Term states.
      this._slideResize = new ResizeObserver(() => {
        cancelAnimationFrame(this._heightFrame);
        this._heightFrame = requestAnimationFrame(() => this.syncViewportHeight());
      });
      this.slides.forEach((slide) => this._slideResize.observe(slide));
      window.PmSwipe?.prepareAdjacent(this.slides, this.index);
      this.render(false);
    }

    disconnectedCallback() {
      this._abort?.abort();
      this.unbindSwipe?.();
      this.unbindSwipe = null;
      this._resize?.disconnect();
      this._slideResize?.disconnect();
      cancelAnimationFrame(this._heightFrame);
      this.viewport?.style.removeProperty('height');
      this._viewportWidth = null;
      clearTimeout(this._unlockTimer);
      this.dragging = false;
      this.busy = false;
      this._ready = false;
    }

    onKeydown(event) {
      let next = null;
      if (event.key === 'ArrowRight') next = this.index + 1;
      if (event.key === 'ArrowLeft') next = this.index - 1;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.slides.length - 1;
      if (next === null) return;
      event.preventDefault();
      this.go(next);
    }

    onDotKeydown(event, currentIndex) {
      let next = currentIndex;
      if (event.key === 'ArrowRight') next = Math.min(this.dots.length - 1, currentIndex + 1);
      else if (event.key === 'ArrowLeft') next = Math.max(0, currentIndex - 1);
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.dots.length - 1;
      else return;

      event.preventDefault();
      this.go(next);
      this.dots[next]?.focus({ preventScroll: true });
    }

    renderDrag(dx) {
      const width = this.viewport.clientWidth || 1;
      const edgeResistance = (this.index === 0 && dx > 0) || (this.index === this.slides.length - 1 && dx < 0);
      const adjusted = edgeResistance ? dx * 0.22 : dx;
      this.track.style.transform = `translate3d(${(-this.index * width) + adjusted}px,0,0)`;
    }

    go(next, fromDrag = false) {
      if (this.busy && !fromDrag) return;
      const clamped = Math.max(0, Math.min(this.slides.length - 1, next));
      if (clamped === this.index) {
        this.render(true);
        return;
      }
      this.index = clamped;
      window.PmSwipe?.prepareAdjacent(this.slides, this.index);
      this.render(true);
    }

    syncViewportHeight() {
      const height = Math.ceil(this.slides[this.index]?.getBoundingClientRect().height || 0);
      if (height > 0 && this.viewport.style.height !== `${height}px`) {
        this.viewport.style.height = `${height}px`;
      }
    }

    render(animate = true) {
      const reduced = this.reduceMotion?.matches;
      this.track.style.transition = animate && !reduced ? 'transform 260ms cubic-bezier(.22,.61,.36,1)' : 'none';
      this.track.style.transform = `translate3d(${-this.index * 100}%,0,0)`;
      this.syncViewportHeight();

      this.slides.forEach((slide, index) => {
        slide.setAttribute('aria-hidden', String(index !== this.index));
      });
      this.dots.forEach((dot, index) => {
        const selected = index === this.index;
        dot.setAttribute('aria-current', String(selected));
        dot.tabIndex = selected ? 0 : -1;
      });
      if (this.status) this.status.textContent = `Health risk ${this.index + 1} of ${this.slides.length}`;

      clearTimeout(this._unlockTimer);
      if (animate && !reduced) {
        this.busy = true;
        this._unlockTimer = setTimeout(() => { this.busy = false; }, 280);
      } else {
        this.busy = false;
      }
    }
  }

  customElements.define(TAG, PMHomeHealthCarousel);
})();
